/** @format */
import React, { useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { getAllUsers, getAllSubs, getSubscriptionEvents } from '../../store/user';
import { getPrices } from '../../store/price';
import Card from '../../components/ui/card';
import ErrorBoundary from '../../components/ui/ErrorBoundary';
import PendingSubmissionsCard from './PendingSubmissionsCard';
import MRRTrendCard from './MRRTrendCard';
import RenewalRadarCard from './RenewalRadarCard';
import TotalSubscriptionsCard from './TotalSubscriptionsCard';
import SubmissionActivityHeatmap from './SubmissionActivityHeatmap';
import { UI_CONSTANTS } from '../../utils/submissionActivityConstants';
import FailingPaymentsCard from './FailingPaymentsCard';
import useFailingPayments from '../../hooks/useFailingPayments';
import PropertyBuildingIcon from '../../components/ui/icons/PropertyBuildingIcon';
import {
	FiPackage,
	FiCreditCard,
	FiTrendingUp,
	FiCalendar,
	FiDollarSign,
} from 'react-icons/fi';

// Dashboard configuration constants
const DASHBOARD_THRESHOLDS = {
	HIGH_VOLUME_CLIENTS: 50,
	HIGH_VOLUME_SUBSCRIPTIONS: 100,
	URGENT_SUBMISSION_COUNT: 10,
	CRITICAL_SUBMISSION_COUNT: 20,
	HIGH_MRR_THRESHOLD: 50000,
	CRITICAL_RENEWAL_COUNT: 5,
	MAX_DISPLAY_COUNT: 99,
	ACTIVE_CLIENT_PERCENTAGE: 0.75,
	ACTIVE_SUBSCRIPTION_PERCENTAGE: 0.6,
};

const Dashboard = () => {
	const dispatch = useDispatch();
	const token = useSelector((st) => st.admin.token);
	const isSuper = useSelector((st) => st.admin.currentAdmin?.isSuper);
	const prices = useSelector((st) => st.price.allPrices);
	const users = useSelector((st) => st.user.allUsers);
	const subs = useSelector((st) => st.user.allSubs);
	const subscriptionEvents = useSelector((st) => st.user.subscriptionEvents);
	const isLoadingUsers = useSelector((st) => st.user.pending);
	const isLoadingPrices = useSelector((st) => st.price.pending);

	// Fetch financial data only for super admins
	// Submission cards fetch their own data independently
	useEffect(() => {
		if (token && isSuper) {
			dispatch(getAllUsers({ token }));
			dispatch(getPrices({ token }));
			dispatch(getAllSubs({ token }));
			dispatch(getSubscriptionEvents({ token, days: 180 }));
		}
	}, [token, dispatch, isSuper]);

	// Calculate stats with memoization
	const totalRevenue = useMemo(() => {
		return subs.reduce((acc, sub) => {
			const price = prices.find((p) => p.id === sub.productId);
			return acc + (price ? price.price / 100 : 0);
		}, 0);
	}, [subs, prices]);
	
	// Extract failing payments from subscriptions using custom hook
	const failingPayments = useFailingPayments(subs, users, prices);

	// Calculate Monthly Recurring Revenue (MRR) from active subscriptions - memoized
	const calculateMRR = useCallback(() => {
		try {
			if (!Array.isArray(subs)) {
				console.warn('Invalid subs data for MRR calculation');
				return 0;
			}

			return subs.reduce((acc, sub) => {
				// Only count active subscriptions
				if (!sub || sub.status !== 'active') return acc;
				
				// Access price data from Stripe subscription structure
				const priceItem = sub.items?.data?.[0]?.price;
				if (!priceItem) {
					console.warn(`Price item not found in subscription ${sub.id}`);
					return acc;
				}
				
				// Get the unit amount from the price (in cents)
				const unitAmount = priceItem.unit_amount || 0;
				if (unitAmount <= 0) {
					console.warn(`Invalid unit amount for subscription ${sub.id}:`, unitAmount);
					return acc;
				}
				
				// Convert to monthly amount based on billing interval
				let monthlyAmount = unitAmount;
				const interval = priceItem.recurring?.interval;
				
				if (interval === 'year') {
					// Convert yearly to monthly
					monthlyAmount = Math.round(unitAmount / 12);
				} else if (interval !== 'month') {
					console.warn(`Unsupported billing interval ${interval} for subscription ${sub.id}`);
					return acc;
				}
				
				// Convert from cents to dollars and add to MRR
				return acc + (monthlyAmount / 100);
			}, 0);
		} catch (error) {
			console.error('Error calculating MRR:', error);
			return 0;
		}
	}, [subs]);

	const currentMRR = useMemo(() => calculateMRR(), [calculateMRR]);
	
	// Calculate historical MRR trend using subscription events
	const calculateMRRTrend = useCallback(() => {
		if (!Array.isArray(subscriptionEvents) || !Array.isArray(subs)) {
			return [];
		}

		const last6Months = [];
		const now = new Date();
		
		// Generate array of past 6 months (180 days) - using safer date arithmetic
		for (let i = 179; i >= 0; i--) {
			const date = new Date(now.getTime() - (i * 24 * 60 * 60 * 1000));
			date.setHours(23, 59, 59, 999); // Set to end of day to include subscriptions created that day
			
			last6Months.push({
				date: new Date(date),
				dateString: date.toISOString().split('T')[0],
				mrr: 0
			});
		}

		// Optimize: Preprocess cancellation events by subscription ID to avoid O(n³) complexity
		const cancellationEventsBySubscription = subscriptionEvents.reduce((acc, event) => {
			if (event.type === 'customer.subscription.deleted') {
				if (!acc[event.subscription.id]) acc[event.subscription.id] = [];
				acc[event.subscription.id].push(event);
			}
			return acc;
		}, {});

		// For each day, calculate MRR by looking at active subscriptions on that day
		last6Months.forEach((day) => {
			const dayTimestamp = Math.floor(day.date.getTime() / 1000);
			let dayMRR = 0;
			
			// Go through each current subscription and check if it was active on this day
			subs.forEach((sub) => {
				const subCreatedTimestamp = sub.created;
				
				// Check if subscription existed on this day
				if (subCreatedTimestamp <= dayTimestamp) {
					// Check if subscription was cancelled before this day using preprocessed events
					const subscriptionCancellations = cancellationEventsBySubscription[sub.id] || [];
					const wasCancelledBeforeDay = subscriptionCancellations.some(event => 
						event.created <= dayTimestamp
					);
					
					if (!wasCancelledBeforeDay) {
						// Calculate MRR contribution for this subscription
						const priceItem = sub.items?.data?.[0]?.price;
						if (priceItem) {
							let monthlyAmount = priceItem.unit_amount || 0;
							const interval = priceItem.recurring?.interval;
							
							if (interval === 'year') {
								monthlyAmount = Math.round(monthlyAmount / 12);
							} else if (interval !== 'month') {
								monthlyAmount = 0;
							}
							
							dayMRR += (monthlyAmount / 100);
						}
					}
				}
			});
			
			day.mrr = dayMRR;
		});

		return last6Months;
	}, [subscriptionEvents, subs]);

	const mrrTrendData = useMemo(() => calculateMRRTrend(), [calculateMRRTrend]);
	
	// Calculate trend percentage (current vs 6 months ago)
	const trendPercentage = useMemo(() => {
		if (mrrTrendData.length < 2) return 0;
		
		const oldestMRR = mrrTrendData[0]?.mrr;
		const newestMRR = mrrTrendData[mrrTrendData.length - 1]?.mrr;
		
		// Ensure we have valid numbers
		if (typeof oldestMRR !== 'number' || typeof newestMRR !== 'number' || 
			isNaN(oldestMRR) || isNaN(newestMRR)) {
			console.warn('Dashboard: Invalid MRR data for trend calculation');
			return 0;
		}
		
		if (oldestMRR === 0) return newestMRR > 0 ? 100 : 0;
		
		return ((newestMRR - oldestMRR) / oldestMRR) * 100;
	}, [mrrTrendData]);

	// Calculate renewal radar data with real subscription currentPeriodEnd data
	const renewalData = useMemo(() => {
		try {
			if (!Array.isArray(subs) || !Array.isArray(users)) {
				console.warn('Invalid subs or users data for renewal calculation');
				return { urgentRenewals: 0, upcomingRenewals: 0, urgentRenewalsList: [], upcomingRenewalsList: [] };
			}

			const now = new Date();
			const DAYS_TO_MS = 24 * 60 * 60 * 1000; // milliseconds in a day
			const URGENT_DAYS = 7;
			const UPCOMING_DAYS = 30;
			
			const in7Days = new Date(now.getTime() + (URGENT_DAYS * DAYS_TO_MS));
			const in30Days = new Date(now.getTime() + (UPCOMING_DAYS * DAYS_TO_MS));
			
			const urgentRenewalsList = [];
			const upcomingRenewalsList = [];
			
			// Validate subscription data structure
			const validateSubscriptionData = (sub) => {
				return sub && 
					   sub.current_period_end && 
					   sub.status === 'active' &&
					   sub.items?.data?.[0]?.price &&
					   typeof sub.current_period_end === 'number';
			};
			
			// Process each subscription to check for upcoming renewals
			let totalSubs = 0;
			let validSubs = 0;
			let renewalsInWindow = 0;
			
			subs.forEach((sub) => {
				totalSubs++;
				
				if (!validateSubscriptionData(sub)) {
					console.warn('Invalid subscription data skipped:', sub?.id, 'Status:', sub?.status, 'Current period end:', sub?.current_period_end);
					return;
				}
				
				validSubs++;
				const renewalDate = new Date(sub.current_period_end * 1000); // Convert from timestamp
				const daysUntilRenewal = Math.ceil((renewalDate.getTime() - now.getTime()) / DAYS_TO_MS);
				
				console.log(`Subscription ${sub.id}: Renewal in ${daysUntilRenewal} days (${renewalDate.toDateString()})`);
				
				// Only include renewals within next 30 days
				if (renewalDate <= in30Days && daysUntilRenewal >= 0) {
					renewalsInWindow++;
					// Find customer info
					const customer = users.find(user => user.customerId === sub.customer);
					const customerName = customer ? `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || customer.username : 'Unknown Customer';
					
					// Get subscription plan details with safe access
					const priceItem = sub.items.data[0].price; // Already validated above
					const planName = priceItem.nickname || 
									 priceItem.product?.name || 
									 priceItem.id || 
									 'Unknown Plan';
					const amount = typeof priceItem.unit_amount === 'number' ? priceItem.unit_amount : 0;
					
					const renewalInfo = {
						subscriptionId: sub.id,
						customerId: sub.customer,
						customerName,
						username: customer?.username, // Add username for navigation
						planName,
						amount, // in cents
						renewalDate,
						daysUntilRenewal,
						status: sub.status,
						paymentMethodStatus: 'unknown' // Could be enhanced with payment method validation
					};
					
					// Categorize by urgency
					if (renewalDate <= in7Days) {
						urgentRenewalsList.push(renewalInfo);
					} else {
						upcomingRenewalsList.push(renewalInfo);
					}
				}
			});
			
			console.log(`Renewal Summary: ${totalSubs} total subs, ${validSubs} valid subs, ${renewalsInWindow} renewals in 30-day window`);
			
			// Sort by renewal urgency (earliest first)
			urgentRenewalsList.sort((a, b) => a.daysUntilRenewal - b.daysUntilRenewal);
			upcomingRenewalsList.sort((a, b) => a.daysUntilRenewal - b.daysUntilRenewal);
			
			return {
				urgentRenewals: urgentRenewalsList.length,
				upcomingRenewals: upcomingRenewalsList.length,
				urgentRenewalsList,
				upcomingRenewalsList
			};
		} catch (error) {
			console.error('Error calculating renewal data:', error);
			return { urgentRenewals: 0, upcomingRenewals: 0, urgentRenewalsList: [], upcomingRenewalsList: [] };
		}
	}, [subs, users]);


	// Get current month name
	const currentMonth = new Date().toLocaleString('default', { month: 'long' });

	return (
		<div className='space-y-4 sm:space-y-6 lg:space-y-8'>
			{/* Financial Stats Cards - Super Admin Only */}
			{isSuper && (
				<div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 sm:gap-6 lg:gap-8'>
					{/* Total Users */}
					<Card className='overflow-hidden bg-white border border-gray-100 hover:shadow-lg transition-shadow duration-200'>
						<div className='p-4 sm:p-6 lg:p-8'>
							<div className='flex justify-between items-start mb-4'>
								<div>
									<h3 className='text-sm font-montserrat-medium text-gray-700 uppercase tracking-wide'>
										Client Count
									</h3>
									<p className='text-2xl sm:text-3xl lg:text-4xl font-glacial text-gray-900 mt-2'>
										{users.length}
									</p>
								</div>
								<div className='rounded-full p-2 sm:p-3 lg:p-4 bg-gray-100'>
									<PropertyBuildingIcon
										className='h-5 w-5 sm:h-6 sm:w-6 lg:h-7 lg:w-7 text-clientBlack'
										count={Math.min(users.length, DASHBOARD_THRESHOLDS.MAX_DISPLAY_COUNT)}
										showPinkAccent={users.length > DASHBOARD_THRESHOLDS.HIGH_VOLUME_CLIENTS}
									/>
								</div>
							</div>

							{/* Micro-trend indicator */}
							<div className='flex items-center space-x-2 mb-4'>
								<span className='text-sm font-montserrat-regular text-gray-600'>
									<span className='font-montserrat-medium text-green-600'>+12%</span>
									{' '}this month
								</span>
							</div>

							{/* Contextual insight */}
							<div className='pt-4 border-t border-gray-100'>
								<span className='text-xs font-montserrat-regular text-gray-500'>
									{Math.floor(users.length * DASHBOARD_THRESHOLDS.ACTIVE_CLIENT_PERCENTAGE)} active this month
								</span>
							</div>
						</div>
					</Card>

					{/* Total Subscriptions */}
					<ErrorBoundary title="Subscriptions Error" message="Unable to load subscription data.">
						<TotalSubscriptionsCard
							totalSubscriptions={subs.length}
							isLoading={isLoadingUsers}
						/>
					</ErrorBoundary>

					{/* Monthly Recurring Revenue (MRR) */}
					<ErrorBoundary title="Revenue Error" message="Unable to load revenue data.">
						<MRRTrendCard
							currentMRR={currentMRR}
							trendPercentage={trendPercentage}
							mrrTrendData={mrrTrendData}
							isLoading={isLoadingPrices || isLoadingUsers}
						/>
					</ErrorBoundary>

					{/* Renewal Radar */}
					<ErrorBoundary title="Renewals Error" message="Unable to load renewal data.">
						<RenewalRadarCard
							urgentRenewals={renewalData.urgentRenewals}
							upcomingRenewals={renewalData.upcomingRenewals}
							urgentRenewalsList={renewalData.urgentRenewalsList}
							upcomingRenewalsList={renewalData.upcomingRenewalsList}
							isLoading={isLoadingUsers}
						/>
					</ErrorBoundary>
				</div>
			)}

			{/* Submission Cards - All Admins */}
			{isSuper ? (
				<div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-4 sm:gap-6 lg:gap-8'>
					<div className='lg:col-span-4'>
						<ErrorBoundary title="Pending Submissions Error" message="Unable to load pending submissions data.">
							<PendingSubmissionsCard />
						</ErrorBoundary>
					</div>
					<div className='sm:col-span-2 lg:col-span-8'>
						<ErrorBoundary title="Activity Heatmap Error" message="Unable to load submission activity data.">
							<SubmissionActivityHeatmap daysRange={UI_CONSTANTS.defaultDaysRange} />
						</ErrorBoundary>
					</div>
				</div>
			) : (
				<div className='space-y-4 sm:space-y-6 lg:space-y-8'>
					<ErrorBoundary title="Pending Submissions Error" message="Unable to load pending submissions data.">
						<PendingSubmissionsCard />
					</ErrorBoundary>
					<ErrorBoundary title="Activity Heatmap Error" message="Unable to load submission activity data.">
						<SubmissionActivityHeatmap daysRange={UI_CONSTANTS.defaultDaysRange} />
					</ErrorBoundary>
				</div>
			)}

			{/* Failing Payments - Super Admin Only */}
			{isSuper && (
				<div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-4 sm:gap-6 lg:gap-8'>
					<div className='lg:col-span-4 lg:col-start-1'>
						<ErrorBoundary title="Failing Payments Error" message="Unable to load payment failure data.">
							<FailingPaymentsCard
								failingPayments={failingPayments}
								isLoading={isLoadingUsers}
							/>
						</ErrorBoundary>
					</div>
				</div>
			)}
		</div>
	);
};

Dashboard.propTypes = {
	// Dashboard component doesn't receive props directly, but documents expected Redux state shape
	// This component relies on Redux state for all data
};

export default Dashboard;
